// List/derivation logic plus schema import/export, shared by the Models page table and the Model Garden card grid. Kept out of the components so the rules are testable without rendering.
import { generateClient } from "aws-amplify/api";
import type { IconType } from "react-icons";
import { FiCloudRain, FiDollarSign, FiDroplet, FiSun, FiThermometer, FiWind, FiZap } from "react-icons/fi";

import {
  runScheduleToScheduleConfig,
  summarizeScheduleConfig,
} from "../components/Schedule/scheduleConfig";
import {
  getProcessedModelSchemaForExport,
  listProcessedModelSchemasWithForecastRecords,
} from "../graphql/customQueries";

export type ModelSortKey = "recent" | "name" | "created" | "status";

// Name is first and is the page default — run history arrives after the cards, so defaulting to "recent" would paint them in DynamoDB scan order then reshuffle once the run queries land.
export const DEFAULT_MODEL_SORT: ModelSortKey = "name";

export const MODEL_SORT_OPTIONS: { value: ModelSortKey; label: string }[] = [
  { value: "name", label: "Name A–Z" },
  { value: "recent", label: "Recently run" },
  { value: "created", label: "Newest" },
  { value: "status", label: "Status" },
];

export interface ModelSchemaLike {
  id: string;
  name?: string;
  description?: string;
  type?: string;
  schemaType?: string;
  version?: string;
  isActive?: boolean;
  runSchedule?: unknown;
  createdAt?: string;
  owner?: string;
  organizationId?: string;
  /** HISTORICAL models process a dataset's history (stage 4 screens); null = forecast */
  modelKind?: string | null;
  historicalSettings?: unknown;
  // Present once merged from the forecast-records list; search matches output names too
  outputs?: { items?: { name?: string }[] | null } | null;
}

// The template fields the list surfaces read. Deliberately partial — modelSchemaTemplateSlice is untyped JS, so anything absent from the record reads as undefined rather than throwing.
export interface ModelSchemaTemplateLike {
  id?: string;
  name?: string;
  category?: string;
  requiresTraining?: boolean;
  trainingCost?: number;
  /** HISTORICAL templates process a dataset's history; null = forecast */
  modelKind?: string | null;
  /** External forecast templates have no processor: forecasts are uploaded by the model's owner or organization, and the model never gets a schedule */
  external?: boolean | null;
}

// Templates keyed by the schemaType that schemas link to them with.
export type TemplatesById = Record<string, ModelSchemaTemplateLike | undefined>;

export interface LatestRunLike {
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

// The run query failed — distinct from null ("never ran"), which would be a false claim.
export const RUN_LOAD_FAILED = "error" as const;

// undefined = still loading, null = never ran, RUN_LOAD_FAILED = lookup failed
export type LatestRunState = LatestRunLike | null | undefined | typeof RUN_LOAD_FAILED;

// Card bands use the existing Erode palette rather than one-off shades — only the mid/dark tokens, since the band carries a white Erode mark and the pale ones wouldn't hold contrast. Order is load-bearing: getBandColor indexes by id hash, so reordering recolours every card. hero-accent and teal-mist are deliberately absent — both fall under 2.5:1 against the white mark, so it washes out. Add them back only if variety matters more than the mark staying legible.
const BAND_COLORS = [
  "bg-eai-cta-primary",
  "bg-eai-slash",
  "bg-eai-teal-deep",
  "bg-eai-surface-dark",
];

// Deterministic band colour for a card, keyed off the id's first segment.
export function getBandColor(id: string): string {
  const firstSegment = (id || "").split("-")[0] || id || "";
  const sum = firstSegment.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return BAND_COLORS[sum % BAND_COLORS.length];
}

// Category for a schema, resolved from its template (schemas carry no category of their own).
export function getModelCategory(
  model: ModelSchemaLike,
  templatesById: TemplatesById
): string | undefined {
  return templatesById?.[model.schemaType || ""]?.category || undefined;
}

// Template display name, falling back to the schema's own type label.
export function getTemplateName(
  model: ModelSchemaLike,
  templatesById: TemplatesById
): string {
  return templatesById?.[model.schemaType || ""]?.name || model.type || model.schemaType || "—";
}

// Every category present across the given schemas, sorted, for the filter chips.
export function getAvailableCategories(
  models: ModelSchemaLike[],
  templatesById: TemplatesById
): string[] {
  const set = new Set<string>();
  models.forEach((m) => {
    const category = getModelCategory(m, templatesById);
    if (category) set.add(category);
  });
  return Array.from(set).sort();
}

// Human schedule label ("Daily 6:00 AM"), or "Manual" when the schema has no usable runSchedule
export function describeSchedule(runSchedule: unknown): string {
  if (!runSchedule) return "Manual";
  try {
    // Parse here rather than letting runScheduleToScheduleConfig swallow a bad string — it falls back to a Daily 9:00 AM default, which would show a schedule the model doesn't actually have.
    let parsed: any = runSchedule;
    if (typeof runSchedule === "string") {
      parsed = JSON.parse(runSchedule);
    }
    if (!parsed || typeof parsed !== "object" || !parsed.frequency) return "Manual";

    const cfg = runScheduleToScheduleConfig(parsed);
    // A "once" config with no time is an unset schedule, not a real one-off run.
    if (cfg.mode === "once" && !cfg.firstRunTime) return "Manual";
    if (cfg.mode === "custom" && !cfg.cronExpression) return "Manual";
    return summarizeScheduleConfig(cfg);
  } catch {
    return "Manual";
  }
}

// Relative age of a run, e.g. "Ran 2h ago".
function timeAgo(iso?: string): string {
  if (!iso) return "Ran recently";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Ran recently";
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 60) return `Ran ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `Ran ${hours}h ago`;
  return `Ran ${Math.round(hours / 24)}d ago`;
}

// Footer label + tone for a last run; undefined = still loading, null = never ran
export function describeLastRun(run: LatestRunState): {
  label: string;
  tone: string;
} {
  if (run === undefined) return { label: "…", tone: "text-eai-ink-faint" };
  if (run === RUN_LOAD_FAILED) return { label: "Last run unavailable", tone: "text-eai-ink-faint" };
  if (run === null) return { label: "Never run", tone: "text-eai-ink-faint" };

  const when = timeAgo(run.updatedAt || run.createdAt);
  const status = String(run.status || "").toUpperCase();
  if (status === "FAILED") return { label: `${when} · failed`, tone: "text-eai-danger-text" };
  if (status === "PROCESSING") return { label: "Running now", tone: "text-eai-warning-text" };
  return { label: when, tone: "text-eai-ink-subtle" };
}

// Sorts never-run, still-loading and failed-lookup rows last. Must stay finite — an -Infinity sentinel made every comparison against such a row NaN/Infinity, collapsing the sort to name.
const NO_RUN_TIMESTAMP = -1;

function runTimestamp(run: LatestRunState): number {
  const iso = typeof run === "object" ? run?.updatedAt || run?.createdAt : undefined;
  if (!iso) return NO_RUN_TIMESTAMP;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? NO_RUN_TIMESTAMP : t;
}

// Cards resolve their run + owner one query each, so a large grid would otherwise fire hundreds of AppSync requests from a single mount — enough to saturate the browser's connection queue and draw throttle errors, which surface as "Last run unavailable" on runs that actually exist.
const FETCH_CONCURRENCY = 8;

// Map over items with at most `limit` requests in flight. Results keep the input order. `worker` must resolve for every item — callers cache their own failures, so a rejection here would lose the whole batch.
export async function mapWithConcurrency<T, R>(
  items: T[],
  worker: (item: T) => Promise<R>,
  limit: number = FETCH_CONCURRENCY
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await worker(items[index]);
    }
  });
  await Promise.all(runners);
  return results;
}

export interface FilterAndSortArgs {
  models: ModelSchemaLike[];
  templatesById: TemplatesById;
  search: string;
  categories: string[];
  sortBy: ModelSortKey;
  latestRuns: Record<string, LatestRunState>;
}

// Filter by search + category chips, then sort. Search spans name, description and schemaType
export function filterAndSortModels({
  models,
  templatesById,
  search,
  categories,
  sortBy,
  latestRuns,
}: FilterAndSortArgs): ModelSchemaLike[] {
  const q = (search || "").trim().toLowerCase();

  let result = (models || []).filter((m) => {
    if (categories?.length) {
      const category = getModelCategory(m, templatesById);
      if (!category || !categories.includes(category)) return false;
    }
    if (!q) return true;
    const outputNames = (m.outputs?.items || []).map((o) => o?.name);
    const haystack = [m.name, m.description, m.schemaType, m.type, ...outputNames]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });

  result = [...result].sort((a, b) => {
    if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
    if (sortBy === "created") {
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    }
    if (sortBy === "status") {
      const rank = (m: ModelSchemaLike) => (m.isActive ? 0 : 1);
      return rank(a) - rank(b) || (a.name || "").localeCompare(b.name || "");
    }
    // recent — most recently run first, never-run last; name breaks ties so the order is stable while run history is still loading rather than falling back to raw fetch order
    const byRun = runTimestamp(latestRuns?.[b.id]) - runTimestamp(latestRuns?.[a.id]);
    if (byRun !== 0) return byRun;
    return (a.name || "").localeCompare(b.name || "");
  });

  return result;
}

// Cap the import so a huge file can't lock the tab while file.text() buffers it
export const MAX_SCHEMA_IMPORT_BYTES = 5 * 1024 * 1024;

// Read an exported schema JSON. Throws with a user-ready message rather than handing the wizard something it can't pre-fill from — JSON.parse happily returns null, numbers and arrays.
export async function parseSchemaImportFile(file: File): Promise<Record<string, any>> {
  if (file.size > MAX_SCHEMA_IMPORT_BYTES) {
    const mb = Math.floor(MAX_SCHEMA_IMPORT_BYTES / 1024 / 1024);
    throw new Error(`That file is too large to import (limit ${mb}MB).`);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(await file.text());
  } catch (err: any) {
    throw new Error(`Could not read schema file: ${err?.message || "invalid JSON"}`);
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("That file isn't a model schema export.");
  }
  const schema = parsed as Record<string, any>;
  // Both are optional on the wizard side, but a file with neither is not a schema export
  if (!schema.name && !schema.schemaType) {
    throw new Error("That file isn't a model schema export — it has no name or schema type.");
  }
  return schema;
}

// Strip path separators and punctuation so a schema name can't shape the download path
export function safeFileSegment(name?: string, fallback = "model"): string {
  return (
    (name || fallback).replace(/\s+/g, "_").replace(/[^\w.-]/g, "").toLowerCase() || fallback
  );
}

// Attach before clicking and revoke on the next tick — revoking synchronously races the download in Safari/Firefox, and a detached anchor is ignored outright by some browsers.
export function downloadJson(contents: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Fetch a schema's full record and download it as JSON. Throws with a user-ready message.
export async function exportModelSchema(model: ModelSchemaLike): Promise<void> {
  const { data } = (await generateClient().graphql({
    query: getProcessedModelSchemaForExport,
    variables: { id: model.id },
  })) as any;

  const fullSchema = data?.getProcessedModelSchema;
  if (!fullSchema) throw new Error("Failed to export model schema: schema not found.");

  const exportData = {
    ...fullSchema,
    exportedAt: new Date().toISOString(),
    exportedBy: "ERODE Platform",
  };
  const safeName = safeFileSegment(model.name);
  downloadJson(JSON.stringify(exportData, null, 2), `schema_${safeName}_${model.id}.json`);
}


// Every schema plus the forecast records behind its outputs, for the output detail modal on Data & Models. Pages through nextToken — a single 100-item page silently dropped the rest.
export async function fetchModelOutputSchemas(): Promise<any[]> {
  const client = generateClient();
  const items: any[] = [];
  let nextToken: string | null = null;
  do {
    const result: any = await client.graphql({
      query: listProcessedModelSchemasWithForecastRecords,
      variables: { limit: 100, nextToken },
    });
    items.push(...(result.data.listProcessedModelSchemas.items || []));
    nextToken = result.data.listProcessedModelSchemas.nextToken;
  } while (nextToken);
  return items;
}

// The All tab sorts datasets and models with one control; map its dataset keys onto the nearest model key
export function toModelSortKey(datasetSortKey: string): ModelSortKey {
  if (datasetSortKey === "recent") return "recent";
  return "name";
}

// Keywords pick the glyph and band on a card's rail, so both say what the card is about. Every band is at least 3:1 against the white glyph (the bar for a graphic, and the one the id-hash bands above already meet). Word boundaries keep "template", "workflow", "wheat", "gridded" and "empower" from matching; order is specific-to-general
const SUBJECTS: { match: RegExp; Icon: IconType; band: string }[] = [
  { match: /\bwind(?!ow)|\bturbine/, Icon: FiWind, band: "bg-eai-slash" },
  { match: /\bsolar|\birradian|\bphotovolt|\bpv\b/, Icon: FiSun, band: "bg-eai-warning-text" },
  { match: /\bhydro|\bwater|\bflow\b|\briver|\bstream(flow)?\b|\bdischarge|\breservoir/, Icon: FiDroplet, band: "bg-eai-teal-deep" },
  { match: /\brain|\bprecip|\bsnow|\bstorm|cloud cover/, Icon: FiCloudRain, band: "bg-eai-info-text" },
  { match: /\btemperature|\btemp\b|\bweather|\bclimate|\bheat\b|degree.?day/, Icon: FiThermometer, band: "bg-eai-danger" },
  { match: /\bprice|\bcost\b|\bmarket|\brevenue/, Icon: FiDollarSign, band: "bg-eai-surface-dark" },
  { match: /\bload\b|\bdemand|\bpower|\benergy|\bgrid\b|\bgenerat(ion|or)|\bmw\b|\belectric/, Icon: FiZap, band: "bg-eai-cta-primary" },
];

// Each text is a separate field, most telling first (category, then name…): the first field with a match decides, so a stray word in a long description or a column name can't outvote the name. Null when nothing matches, so the caller keeps its own fallback glyph and band
export function pickSubject(...fields: Array<string | null | undefined>): { Icon: IconType; band: string } | null {
  for (const field of fields) {
    if (!field) continue;
    const text = field.toLowerCase();
    const hit = SUBJECTS.find(({ match }) => match.test(text));
    if (hit) return { Icon: hit.Icon, band: hit.band };
  }
  return null;
}
